import { z } from 'zod';
import {
  UserRoleSchema,
  ProjectMemberRoleSchema,
  TeamMemberRoleSchema,
} from './common';

// ─── Invitations (admin → new user) ──────────────────────────────────

export const InviteUserRequestSchema = z.object({
  email: z.string().email().max(256),
  name: z.string().min(1).max(128),
  role: UserRoleSchema,
  teamIds: z.array(z.string()).max(20).default([]),
});
export type InviteUserRequest = z.infer<typeof InviteUserRequestSchema>;

export const AcceptInviteRequestSchema = z.object({
  token: z.string().min(32).max(256),
  name: z.string().min(1).max(128),
  password: z.string().min(8, 'Password must be at least 8 characters').max(256),
});
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequestSchema>;

// ─── Admin user edit ─────────────────────────────────────────────────

export const UpdateUserRequestSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  role: UserRoleSchema.optional(),
  teamIds: z.array(z.string()).max(20).optional(),
});
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;

// ─── Teams ──────────────────────────────────────────────────────────

export const CreateTeamRequestSchema = z.object({
  name: z.string().min(1).max(128),
  description: z.string().max(1000).optional(),
  memberIds: z.array(z.string()).max(50).default([]),
});
export type CreateTeamRequest = z.infer<typeof CreateTeamRequestSchema>;

export const UpdateTeamRequestSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  description: z.string().max(1000).nullish(),
});
export type UpdateTeamRequest = z.infer<typeof UpdateTeamRequestSchema>;

export const AddTeamMemberRequestSchema = z.object({
  userId: z.string().min(1),
  teamRole: TeamMemberRoleSchema.default('MEMBER'),
});
export type AddTeamMemberRequest = z.infer<typeof AddTeamMemberRequestSchema>;

// ─── Project members ────────────────────────────────────────────────

export const AddProjectMemberRequestSchema = z.object({
  userId: z.string().min(1),
  role: ProjectMemberRoleSchema.default('COLLABORATOR'),
});
export type AddProjectMemberRequest = z.infer<typeof AddProjectMemberRequestSchema>;

export const UpdateProjectMemberRequestSchema = z.object({
  role: ProjectMemberRoleSchema,
});
export type UpdateProjectMemberRequest = z.infer<typeof UpdateProjectMemberRequestSchema>;

export const TransferProjectRequestSchema = z.object({
  newOwnerId: z.string().min(1),
  keepPreviousAsCollaborator: z.boolean().default(true),
});
export type TransferProjectRequest = z.infer<typeof TransferProjectRequestSchema>;

// ─── Response shapes (used by API client + frontend) ────────────────

export const AdminUserListItemSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: UserRoleSchema,
  status: z.enum(['ACTIVE', 'PENDING_INVITE', 'SUSPENDED']),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  suspendedAt: z.string().nullable(),
  teams: z.array(z.object({ id: z.string(), name: z.string() })),
  pendingInvitationId: z.string().nullable(),
});
export type AdminUserListItem = z.infer<typeof AdminUserListItemSchema>;

export const InvitationListItemSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  invitedRole: UserRoleSchema,
  invitedTeamIds: z.array(z.string()),
  invitedBy: z.string(),
  invitedByName: z.string().nullable(),
  expiresAt: z.string(),
  resentCount: z.number().int().nonnegative(),
  lastSentAt: z.string().nullable(),
  createdAt: z.string(),
  inviteUrl: z.string(),
});
export type InvitationListItem = z.infer<typeof InvitationListItemSchema>;

export const TeamSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  memberCount: z.number().int().nonnegative(),
});
export type Team = z.infer<typeof TeamSchema>;

export const TeamMemberSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: UserRoleSchema,
  teamRole: TeamMemberRoleSchema,
  addedAt: z.string(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const ProjectMemberSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: ProjectMemberRoleSchema,
  userRole: UserRoleSchema,
  addedAt: z.string(),
});
export type ProjectMember = z.infer<typeof ProjectMemberSchema>;

export const InviteInfoSchema = z.object({
  email: z.string(),
  name: z.string(),
  role: UserRoleSchema,
  inviterName: z.string().nullable(),
  companyName: z.string(),
  expiresAt: z.string(),
});
export type InviteInfo = z.infer<typeof InviteInfoSchema>;
